// The Dashboard's reads and card rules: ONE copy, used by the desktop Dashboard (pages/Dashboard.js) and
// the phone (mobile/screens/DashboardScreen.jsx). Dates here are the viewer's LOCAL days, as the
// desktop page has always counted them.
import config from "../../config.js";
import { isDueDateLate, parseDueDate } from "../../utils/dueDate";
import { fetchAllJobPages, fetchJobsPage } from "../../utils/jobPages";

const API_BASE = config.API_BASE_URL;

export const STATIONS = Object.freeze(["Laser", "Tap", "Brake", "Fabrication", "Blast", "Finish"]);

// ── Date and time helpers (moved from pages/Dashboard.js unchanged) ──

/** Decimal hours as "Xh Ym" ("<1m" under a minute). */
export function formatHoursToHM(totalHours) {
  if (typeof totalHours !== "number" || isNaN(totalHours) || totalHours < 0) {
    return "0h 0m";
  }
  const hours = Math.floor(totalHours);
  const minutes = Math.round((totalHours - hours) * 60);
  if (hours === 0 && minutes === 0 && totalHours > 0) {
    return "<1m";
  }
  if (minutes === 60) {
    return `${hours + 1}h 0m`;
  }
  return `${hours}h ${minutes}m`;
}

export function parseIntervalToHours(intervalStr) {
  if (!intervalStr) return 0;
  const parts = intervalStr.split(":");
  if (parts.length < 2) return 0;
  const hh = parseFloat(parts[0]) || 0;
  const mm = parseFloat(parts[1]) || 0;
  const ss = parts[2] ? parseFloat(parts[2]) : 0;
  return hh + mm / 60 + ss / 3600;
}

/** A Date / date string as the LOCAL "YYYY-MM-DD" ("" when it isn't a date). */
export function formatDateNoTime(dateLike) {
  if (dateLike instanceof Date && !isNaN(dateLike)) {
    const y = dateLike.getFullYear();
    const m = String(dateLike.getMonth() + 1).padStart(2, "0");
    const d = String(dateLike.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  }
  if (typeof dateLike === "string" && /^\d{4}-\d{2}-\d{2}$/.test(dateLike)) {
    return dateLike;
  }
  const d = new Date(dateLike);
  if (!isNaN(d.getTime())) {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${dd}`;
  }
  return "";
}

export function parseDateAsLocal(yyyyMMdd) {
  if (!yyyyMMdd) return null;
  const parts = yyyyMMdd.split("-");
  if (parts.length !== 3) return null;
  const y = +parts[0], m = +parts[1] - 1, d = +parts[2];
  const dt = new Date(y, m, d, 0, 0, 0, 0);
  return isNaN(dt.getTime()) ? null : dt;
}

export function getUtcRangeForLocalDate(startDateStr, endDateStr) {
  const startLocal = parseDateAsLocal(startDateStr);
  const endLocal = parseDateAsLocal(endDateStr);
  if (!startLocal || !endLocal) {
    return [new Date("1970-01-01"), new Date("2100-12-31")];
  }
  endLocal.setHours(23, 59, 59, 999);
  const startUtc = new Date(startLocal.getTime() - (startLocal.getTimezoneOffset() * 60000));
  const endUtc = new Date(endLocal.getTime() - (endLocal.getTimezoneOffset() * 60000));
  return [startUtc, endUtc];
}

export function getWeekdayCount(startStr, endStr) {
  const s = parseDateAsLocal(startStr);
  const e = parseDateAsLocal(endStr);
  if (!s || !e) return 0;
  s.setHours(0, 0, 0, 0);
  e.setHours(0, 0, 0, 0);
  let count = 0;
  let cur = new Date(s);
  while (cur <= e) {
    const dayOfWeek = cur.getDay();
    if (dayOfWeek !== 0 && dayOfWeek !== 6) {
      count++;
    }
    cur.setDate(cur.getDate() + 1);
  }
  return count;
}

export function isInLocalDateRange(dateStr, startStr, endStr) {
  if (!dateStr) return false;
  const d = parseDateAsLocal(dateStr);
  const s = parseDateAsLocal(startStr);
  const e = parseDateAsLocal(endStr);
  if (!d || !s || !e) return false;
  return (d >= s && d <= e);
}

export function spreadAcrossLocalMidnights(start, end, callback) {
  if (start >= end) return;
  let cur = new Date(start);
  while (cur < end) {
    const nextLocalMidnight = new Date(cur.getFullYear(), cur.getMonth(), cur.getDate() + 1, 0, 0, 0, 0);
    const chunkEnd = (nextLocalMidnight < end) ? nextLocalMidnight : end;
    const chunkMs = chunkEnd - cur;
    const chunkHrs = chunkMs / 3600000;
    const dayStr = formatDateNoTime(cur);
    callback(dayStr, chunkHrs);
    cur = chunkEnd;
  }
}

// ── The cards' rules ──

/** A job's due DAY as YYYY-MM-DD. jobs.due_date arrives as "2026-10-15T00:00:00Z": a calendar day,
 *  not an instant (utils/dueDate.js). Read through new Date() it was the 14th in Central, so the
 *  Dashboard listed jobs due tomorrow under "Due today" and a job due today as late (Mike, 2026-10-14:
 *  fix it on desktop and phone). */
export function dueDay(due) {
  const d = parseDueDate(due);
  return d ? formatDateNoTime(d) : "";
}

/** P1 & P2 jobs still open, late jobs (Pending / In Progress past due), and jobs due today. */
export function jobAlerts(jobs, now = new Date()) {
  const rows = Array.isArray(jobs) ? jobs : [];
  const todayStr = formatDateNoTime(now);
  return {
    priorityJobs: rows.filter((j) => j.priority_level <= 2 && j.status !== "Shipped" && j.status !== "Completed"),
    // Late = the due DAY is behind today (a job due today is not late yet).
    lateJobs: rows.filter((j) => j.due_date && isDueDateLate(j.due_date, now) && (j.status === "Pending" || j.status === "In Progress")),
    dueTodayJobs: rows.filter((j) => j.due_date && dueDay(j.due_date) === todayStr),
  };
}

/** Late jobs grouped by their due day, { "YYYY-MM-DD": [jobs] } in the order they came. */
export function groupByDueDate(jobs) {
  return (Array.isArray(jobs) ? jobs : []).reduce((acc, j) => {
    const dd = dueDay(j.due_date);
    (acc[dd] = acc[dd] || []).push(j);
    return acc;
  }, {});
}

/** On hand + inbound - reserved. */
export function materialAvailability(m) {
  return (m.stock_quantity || 0) + (m.inbound_quantity || 0) - (m.reserved_quantity || 0);
}

/** Consumables at or under their reorder point; materials with negative availability. */
export function materialAlerts(materials, consumables) {
  const mats = Array.isArray(materials) ? materials : [];
  const cons = Array.isArray(consumables) ? consumables : [];
  return {
    consumablesReorder: cons.filter((c) => (c.stock_quantity || 0) <= (c.reorder_point || 0)),
    materialsNegative: mats.filter((m) => materialAvailability(m) < 0),
  };
}

/** Rows with `flagged` as a real boolean, and the flagged ones. */
export function withFlag(rows) {
  return (Array.isArray(rows) ? rows : []).map((r) => ({ ...r, flagged: !!r.flagged }));
}
export function flaggedOnly(rows) {
  return withFlag(rows).filter((r) => r.flagged);
}

/** RFQ purchase orders grouped by rfq_group_id: [{rfq_group_id, created_at, vendors, line_count}]. */
export function pendingRfqGroups(rfqPos) {
  const groups = {};
  (Array.isArray(rfqPos) ? rfqPos : []).forEach((po) => {
    if (po.rfq_group_id) {
      if (!groups[po.rfq_group_id]) {
        groups[po.rfq_group_id] = {
          rfq_group_id: po.rfq_group_id,
          created_at: po.order_date,
          vendors: [],
          line_count: (po.lines || []).length,
        };
      }
      groups[po.rfq_group_id].vendors.push(po.supplier);
    }
  });
  return Object.values(groups);
}

/**
 * Hours worked per station per local day, from time punches (start/resume → pause/complete, wall
 * clock, split at local midnights): { "YYYY-MM-DD": { Laser: h, ... } }. A punch's own `workstation`
 * wins; `tasks` (task_id → workstation) is the fallback.
 */
export function stationHoursByDay(punches, tasks, startDateStr, endDateStr, stations = STATIONS) {
  const stationDayTotals = stations.reduce((acc, st) => ({ ...acc, [st]: {} }), {});
  const [startUtc, endUtc] = getUtcRangeForLocalDate(startDateStr, endDateStr);
  const taskMap = (Array.isArray(tasks) ? tasks : []).reduce((acc, t) => {
    if (t.task_id != null && t.workstation) acc[t.task_id] = t.workstation;
    return acc;
  }, {});
  const punchesByTask = (Array.isArray(punches) ? punches : []).reduce((acc, p) => {
    const pTime = new Date(p.punch_time);
    if (pTime >= startUtc && pTime <= endUtc) {
      (acc[p.task_id] = acc[p.task_id] || []).push(p);
    }
    return acc;
  }, {});
  for (const tIdStr in punchesByTask) {
    const taskPunches = punchesByTask[tIdStr];
    const station = taskPunches[0]?.workstation || taskMap[parseInt(tIdStr, 10)];
    if (!station || !stations.includes(station)) continue;
    const pArr = taskPunches.sort((a, b) => new Date(a.punch_time) - new Date(b.punch_time));
    let curStart = null;
    pArr.forEach((p) => {
      const pTime = new Date(p.punch_time);
      if (p.punch_type === "start" || p.punch_type === "resume") {
        curStart = pTime;
      } else if ((p.punch_type === "pause" || p.punch_type === "complete") && curStart) {
        let endTime = pTime;
        if (curStart < startUtc) curStart = startUtc;
        if (endTime > endUtc) endTime = endUtc;
        if (endTime > curStart) {
          spreadAcrossLocalMidnights(curStart, endTime, (dayStr, hrs) => {
            stationDayTotals[station][dayStr] = (stationDayTotals[station][dayStr] || 0) + hrs;
          });
        }
        curStart = null;
      }
    });
  }
  const dailyData = {};
  Object.entries(stationDayTotals).forEach(([st, dayObj]) => {
    Object.entries(dayObj).forEach(([dayStr, hrs]) => {
      if (!dailyData[dayStr]) dailyData[dayStr] = stations.reduce((acc, s) => ({ ...acc, [s]: 0 }), {});
      dailyData[dayStr][st] += hrs;
    });
  });
  return dailyData;
}

// ── Reads ──

/**
 * Today's hours per station so far, for the phone: each task's punches paired start/resume ->
 * pause/complete over ALL the punches given (a run that began yesterday still counts its part of
 * today), a run still open counted up to `now`, every run clipped to [local midnight, now]. A punch
 * without a station is skipped. (The desktop chart's stationHoursByDay works on a date window.)
 */
export function stationHoursToday(punches, now = new Date(), stations = STATIONS) {
  const totals = stations.reduce((acc, s) => ({ ...acc, [s]: 0 }), {});
  const dayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const add = (station, from, to) => {
    const a = Math.max(from.getTime(), dayStart.getTime());
    const b = Math.min(to.getTime(), now.getTime());
    if (b > a) totals[station] += (b - a) / 3600000;
  };
  const byTask = (Array.isArray(punches) ? punches : []).reduce((acc, p) => {
    (acc[p.task_id] = acc[p.task_id] || []).push(p);
    return acc;
  }, {});
  Object.values(byTask).forEach((taskPunches) => {
    const station = (taskPunches.find((p) => p.workstation) || {}).workstation;
    if (!station || !stations.includes(station)) return;
    let openedAt = null;
    taskPunches
      .slice()
      .sort((a, b) => new Date(a.punch_time) - new Date(b.punch_time))
      .forEach((p) => {
        const t = new Date(p.punch_time);
        if (p.punch_type === "start" || p.punch_type === "resume") {
          if (!openedAt) openedAt = t;
        } else if ((p.punch_type === "pause" || p.punch_type === "complete") && openedAt) {
          add(station, openedAt, t);
          openedAt = null;
        }
      });
    if (openedAt) add(station, openedAt, now);
  });
  return totals;
}

/** GET a JSON list as {ok, data}: data [] when it fails (the caller says the list couldn't load). */
export async function fetchList(url, label) {
  try {
    const res = await fetch(url);
    if (!res.ok) { console.warn(`${label} returned ${res.status}`); return { ok: false, data: [] }; }
    const data = await res.json();
    return { ok: true, data: Array.isArray(data) ? data : [] };
  } catch (err) {
    console.warn(`${label} failed:`, err);
    return { ok: false, data: [] };
  }
}

/** GET a JSON list; [] (and a console warning) when it fails, so one bad read never empties the page. */
export async function safeFetchJson(url, label) {
  try {
    const res = await fetch(url);
    if (!res.ok) { console.warn(`${label} returned ${res.status}`); return []; }
    return await res.json();
  } catch (err) {
    console.warn(`${label} failed:`, err);
    return [];
  }
}

/** Active jobs as summary rows, page by page (GET /jobs in one response can pass Lambda's 6 MB). */
export function loadActiveJobs() {
  return fetchAllJobPages((page) => fetchJobsPage(API_BASE, "status=Active&summary=true", page))
    .catch((err) => { console.warn("jobs failed:", err); return []; });
}

export const loadMaterials = () => safeFetchJson(`${API_BASE}/materials`, "materials");
export const loadConsumables = () => safeFetchJson(`${API_BASE}/consumables`, "consumables");
export const loadParts = () => safeFetchJson(`${API_BASE}/parts`, "parts");
export const loadCutSheets = () => safeFetchJson(`${API_BASE}/cut_sheets`, "cut_sheets");
export const loadRfqPurchaseOrders = () => safeFetchJson(`${API_BASE}/purchase_orders?status=RFQ`, "rfqs");
export const loadTimePunches = (days) =>
  safeFetchJson(`${API_BASE}/time_punches?days=${days}&include_workstation=true&exclude_maintenance=true`, "time_punches");

const DASHBOARD_LISTS = Object.freeze({
  materials: "materials",
  consumables: "consumables",
  parts: "parts",
  cut_sheets: "cut sheets",
  rfqs: "RFQs",
  time_punches: "station hours",
});

/**
 * Everything the alert cards need, for the phone: each card's rows, today's hours per station (the
 * last 2 days of punches: a task running across midnight still counts its part of today) and
 * `failed`, the lists that couldn't be read (their cards would otherwise read "all clear"). Throws
 * when nothing could be read at all.
 */
export async function loadDashboardCards(now = new Date()) {
  let jobsOk = true;
  const jobsRead = fetchAllJobPages((page) => fetchJobsPage(API_BASE, "status=Active&summary=true", page))
    .catch((err) => { console.warn("jobs failed:", err); jobsOk = false; return []; });
  const [jobs, mats, cons, parts, sheets, rfqs, punches] = await Promise.all([
    jobsRead,
    fetchList(`${API_BASE}/materials`, "materials"),
    fetchList(`${API_BASE}/consumables`, "consumables"),
    fetchList(`${API_BASE}/parts`, "parts"),
    fetchList(`${API_BASE}/cut_sheets`, "cut_sheets"),
    fetchList(`${API_BASE}/purchase_orders?status=RFQ`, "rfqs"),
    fetchList(`${API_BASE}/time_punches?days=2&include_workstation=true&exclude_maintenance=true`, "time_punches"),
  ]);
  const reads = { materials: mats, consumables: cons, parts, cut_sheets: sheets, rfqs, time_punches: punches };
  const failed = [
    ...(jobsOk ? [] : ["jobs"]),
    ...Object.entries(reads).filter(([, r]) => !r.ok).map(([k]) => DASHBOARD_LISTS[k]),
  ];
  if (failed.length === Object.keys(reads).length + 1) throw new Error("Couldn't reach JobQ.");
  return {
    ...jobAlerts(jobs, now),
    ...materialAlerts(mats.data, cons.data),
    flaggedParts: flaggedOnly(parts.data),
    flaggedCutSheets: flaggedOnly(sheets.data),
    pendingRfqs: pendingRfqGroups(rfqs.data),
    stationHoursToday: stationHoursToday(punches.data, now),
    failed,
  };
}
